import { createApp } from 'vue';
import PortalVue from 'portal-vue';
import axios from 'axios';
import 'nodelist-foreach-polyfill';
import { initCookies } from './cookies';
import logger from './logger';
import { IpsEvents } from '@iphoneps/events';
import platformDataHydrator from './ipsevents';
import i18n from './i18n';
import { ObserveVisibility } from 'vue-observe-visibility';
import vueClickOutsideElement from 'vue-click-outside-element';
import VueSimpleAlert from 'vue3-simple-alert';
import store from './checkout/store';
import mitt from 'mitt';
import Tracker from './checkout/tracking/tracker';
import './video-overlay-handler';
import languageWatcher from './plugins/languageWatcher';

window.logger = logger;

const ipse = new IpsEvents(platformDataHydrator, 'https://events.iphonephotographyschool.com/v1/collect', logger);
window.ipse = ipse;

// Log the GIT commit to the console
if (process.env.MIX_GIT_COMMIT) {
    console.log(process.env.MIX_GIT_COMMIT);
}

axios.defaults.headers.common = {
    'x-ipse-aid': platformDataHydrator.resolveAnonymousIdFn(),
    'x-lang': window.App?.language ?? 'en',
};

// Update axios language header when language changes
window.addEventListener('languageChanged', (event) => {
    axios.defaults.headers.common['x-lang'] = event.detail.newLanguage;
});

axios.interceptors.response.use(
    function (response) {
        if (!response.data.success) {
            return Promise.reject(response.data);
        }
        return response;
    },
    function (error) {
        return Promise.reject(error);
    },
);

const eventBus = mitt();

import CheckoutPopup from './checkout/components/CheckoutPopup.vue';
import OpenCheckoutPopup from './checkout/components/buttons/OpenCheckoutPopup.vue';
import ImmediatePaymentButton from './checkout/components/buttons/ImmediatePaymentButton.vue';
import ImmediatePaymentProcessing from './checkout/components/loaders/ImmediatePaymentProcessing.vue';
import UpdatePaymentMethod from './checkout/components/UpdatePaymentMethod.vue';
import UpdateEmailDelay from './checkout/components/UpdateEmailDelay.vue';
import LifetimeUpdates from './checkout/components/LifetimeUpdates.vue';
import RefreshPage from './components/RefreshPage.vue';
import FixedTopBar from './components/FixedTopBar.vue';
import FixedTopBarNoTimer from './components/FixedTopBarNoTimer.vue';
import IosBackButton from './components/IosBackButton.vue';
import InPageTimer from './components/InPageTimer.vue';
import SmallHeader from './components/SmallHeader.vue';
import FloatingSalesButton from './components/FloatingSalesButton.vue';
import ProductImage from './components/ProductImage.vue';
import FaqElement from './components/FaqElement.vue';
import ProductPrice from './components/ProductPrice.vue';
import ProductBonusPrice from './components/ProductBonusPrice.vue';
import CurrencyText from './components/CurrencyText.vue';
import ErrorContextWatcher from './components/ErrorContextWatcher.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import PhoneSlider from './ipa/components/PhoneSlider.vue';
import Carousel from './ipa/components/Carousel.vue';
import CarouselAap from './aap/components/CarouselAap.vue';
import IgaCarousel from './iga/components/Carousel.vue';
import StackCarousel from './components/StackCarousel.vue';
import ScrollTracker from './components/ScrollTracker.vue';
import IosPwaViewListeners from './components/IosPwaViewListeners.vue';
import PaymentCards from './components/PaymentCards.vue';
import Topics from './components/Topics.vue';
import LimitedSpots from './components/LimitedSpots.vue';
import LimitedSpotsNest from './components/LimitedSpotsNest.vue';
import LimitedSpotsInPage from './components/LimitedSpotsInPage.vue';

const loadVue = () => {
    document.querySelectorAll('.vue-instance-wrapper').forEach((el) => {
        const app = createApp();

        app.use(store);
        app.use(PortalVue);
        app.use(VueSimpleAlert);
        app.use(vueClickOutsideElement);
        app.use(i18n);
        app.use(languageWatcher);

        app.directive('observe-visibility', ObserveVisibility);

        app.component('checkout-popup', CheckoutPopup);
        app.component('open-checkout-popup', OpenCheckoutPopup);
        app.component('immediate-payment-button', ImmediatePaymentButton);
        app.component('immediate-payment-processing', ImmediatePaymentProcessing);
        app.component('update-payment-method', UpdatePaymentMethod);
        app.component('update-email-delay', UpdateEmailDelay);
        app.component('lifetime-updates', LifetimeUpdates);
        app.component('refresh-page', RefreshPage);
        app.component('error-context-watcher', ErrorContextWatcher);
        app.component('language-switcher', LanguageSwitcher);
        app.component('fixed-top-bar', FixedTopBar);
        app.component('fixed-top-bar-no-timer', FixedTopBarNoTimer);
        app.component('ios-back-button', IosBackButton);
        app.component('in-page-timer', InPageTimer);
        app.component('small-header', SmallHeader);
        app.component('floating-sales-button', FloatingSalesButton);
        app.component('product-image', ProductImage);
        app.component('faq-element', FaqElement);
        app.component('product-price', ProductPrice);
        app.component('product-bonus-price', ProductBonusPrice);
        app.component('currency-text', CurrencyText);
        app.component('phone-slider', PhoneSlider);
        app.component('carousel', Carousel);
        app.component('carousel-aap', CarouselAap);
        app.component('iga-carousel', IgaCarousel);
        app.component('stack-carousel', StackCarousel);
        app.component('scroll-tracker', ScrollTracker);
        app.component('ios-pwa-view-listeners', IosPwaViewListeners);
        app.component('payment-cards', PaymentCards);
        app.component('topics', Topics);
        app.component('limited-spots', LimitedSpots);
        app.component('limited-spots-nest', LimitedSpotsNest);
        app.component('limited-spots-in-page', LimitedSpotsInPage);

        app.config.globalProperties.ApplePaySession = window.ApplePaySession;
        app.config.globalProperties.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
        );
        app.config.globalProperties.isIosView = window.App.isIosView;
        app.config.globalProperties.isCrowdin = window.App.appEnvironment === 'l10n';
        app.config.globalProperties.tracker = new Tracker();
        // eventBus is used for everything that we consider hack
        app.config.globalProperties.eventBus = eventBus;

        logger.init({
            dsn: 'https://d893498501004a53b65b5c01459922db@o447795.ingest.sentry.io/5428658',
            environment: window.App.appEnvironment,
            release: process.env.MIX_GIT_COMMIT,
            app: app,
        });

        app.mount(el);
    });
};

window.eventBus = eventBus;

window.showLifetimeUpdatesPopup = function () {
    window.eventBus.emit('IOSVIEW_OPEN_LIFETIME_UPDATES_POPUP');
};

initCookies(loadVue);
